/**
 * Shared Simplified V2 screen adapter.
 *
 * This is the single boundary for flows that need to move between the AI-friendly
 * Simplified V2 content shape and full styled V2 StepNodes.
 */

import type {
  SimplifiedV2Content,
  SimplifiedV2StepNode,
} from '@segmently/schema-models/simplified';
import type { StepNode } from '@segmently/schema-models/v2';
import { SimplifiedV2Mapper, simplifiedV2Mapper } from './SimplifiedV2Mapper';
import { V2TemplateStructMerger, v2TemplateStructMerger } from './V2TemplateStructMerger';

export interface SimplifiedV2ToFullInput {
  simplified: SimplifiedV2StepNode;
  lang: string;
  template?: StepNode;
}

export class SimplifiedV2ScreenAdapter {
  constructor(
    private readonly mapper: SimplifiedV2Mapper = simplifiedV2Mapper,
    private readonly merger: V2TemplateStructMerger = v2TemplateStructMerger,
  ) {}

  toFullStepNode(input: SimplifiedV2ToFullInput): StepNode {
    const { simplified, lang, template } = input;
    const full = template
      ? this.merger.merge(template, simplified.content, lang)
      : this.mapper.toStepNode(simplified, lang);

    full.id = simplified.id;
    full.name = simplified.name ?? full.name ?? simplified.id;
    full.screenType = simplified.screenType as StepNode['screenType'];
    full.position = simplified.position ?? full.position ?? { x: 0, y: 0 };

    return full;
  }

  mergeContent(template: StepNode, content: SimplifiedV2Content, lang: string): StepNode {
    return this.merger.merge(template, content, lang);
  }

  toSimplified(stepNode: StepNode, lang: string): SimplifiedV2StepNode {
    return this.mapper.toSimplified(stepNode, lang);
  }

  isSimplified(node: unknown): boolean {
    return this.mapper.isSimplified(node);
  }
}

export const simplifiedV2ScreenAdapter = new SimplifiedV2ScreenAdapter();
